import { MutableRefObject, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import YouTubeIcon from '@mui/icons-material/YouTube';
import CustomButton from 'src/components/atoms/buttons/CustomButton';

export type VideoDialogItem = {
    title: string;
    url: string;
};

export type VideoDialogInput = string | VideoDialogItem | Array<string | VideoDialogItem>;

export type VideoDialogRef = MutableRefObject<{
    url: string;
    videos: VideoDialogItem[];
    open: (video?: VideoDialogItem) => void;
    close: () => void;
} | null>;

const normalizeVideos = (video?: VideoDialogInput): VideoDialogItem[] => {
    if (!video) return [];

    if (typeof video === 'string') return [{ title: '', url: video }];

    if (Array.isArray(video)) {
        return video.map((videoItem) => typeof videoItem === 'string' ? { title: '', url: videoItem } : videoItem);
    }

    return [video];
};

export const useVideoDialog = (video?: VideoDialogInput): VideoDialogRef => {
    const videos = useMemo(() => normalizeVideos(video), [video]);
    const videoRef = useRef<VideoDialogRef['current']>({
        url: videos[0]?.url ?? '',
        videos,
        open: () => undefined,
        close: () => undefined,
    });

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.url = videos[0]?.url ?? '';
            videoRef.current.videos = videos;
        }
    }, [videos]);

    return videoRef;
};

export const VideoButton = ({ videoRef }: { videoRef: VideoDialogRef }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const videos = videoRef.current?.videos ?? [];

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        const currentVideos = videoRef.current?.videos ?? [];

        if (currentVideos.length > 1) {
            setAnchorEl(event.currentTarget);
            return;
        }

        videoRef.current?.open(currentVideos[0]);
    };

    const handleClose = () => setAnchorEl(null);

    const handleVideoClick = (video: VideoDialogItem) => {
        handleClose();
        videoRef.current?.open(video);
    };

    return (
        <>
            <CustomButton title="" iconButton={true} icon={<YouTubeIcon />} onClick={handleClick} />

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {videos.map((video, i) => (
                    <MenuItem key={`${video.url}-${i}`} onClick={() => handleVideoClick(video)}>
                        <ListItemIcon>
                            <YouTubeIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{video.title || `Video ${i + 1}`}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export const VideoDialog = ({ videoRef }: { videoRef: VideoDialogRef }) => {
    const [open, setOpen] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState<VideoDialogItem | null>(null);

    const handleOpen = (video?: VideoDialogItem) => {
        const currentVideos = videoRef.current?.videos ?? [];
        setSelectedVideo(video ?? currentVideos[0] ?? null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedVideo(null);
    };

    videoRef.current = {
        url: videoRef.current?.url ?? selectedVideo?.url ?? '',
        videos: videoRef.current?.videos ?? [],
        open: handleOpen,
        close: handleClose,
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogContent sx={{ p: 0, position: 'relative' }}>
                <IconButton
                    onClick={handleClose}
                    sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: '#fff' }}
                >
                    <CloseIcon />
                </IconButton>

                {selectedVideo && (
                    <iframe
                        width="100%"
                        height="700"
                        src={selectedVideo.url}
                        title={selectedVideo.title || 'YouTube video player'}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};